import { Component } from 'react';
import { useT } from '../i18n';

/**
 * ErrorBoundary — catches React render errors and shows a fallback UI
 * instead of unmounting the entire app (which causes a blank/black screen).
 */
class ClientErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error('[ErrorBoundary] Caught:', error);
    console.error('[ErrorBoundary] Component stack:', info?.componentStack);
  }

  handleReload = () => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  handleGoBack = () => {
    this.setState({ hasError: false, error: null });
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      const t = this.props.t || ((k) => k);
      const msg = this.state.error?.message || t('common.unknown_error');
      return (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            backgroundColor: 'var(--bg-page, #0f1117)',
            color: 'var(--text-primary, #e2e8f0)',
            fontFamily: 'system-ui, sans-serif',
            padding: '24px',
          }}
        >
          <div style={{ maxWidth: 420, textAlign: 'center' }}>
            <div
              style={{
                width: 56, height: 56, borderRadius: '50%',
                backgroundColor: 'var(--bg-hover, #2a2d3e)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 20px', fontSize: 28,
              }}
            >
              ⚠️
            </div>
            <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>
              {t('common.error_title')}
            </h2>
            <p
              style={{
                fontSize: 13, lineHeight: 1.5, marginBottom: 4,
                color: 'var(--text-muted, #718096)',
                wordBreak: 'break-word',
              }}
            >
              {msg}
            </p>
            <p style={{ fontSize: 12, color: 'var(--text-muted, #718096)', marginBottom: 24 }}>
              {t('common.error_persist_hint')}
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
              <button
                onClick={this.handleGoBack}
                style={{
                  padding: '10px 20px', borderRadius: 8, border: '1px solid var(--border, #2a2d3e)',
                  background: 'transparent', color: 'var(--text-secondary, #a0aec0)',
                  fontSize: 13, fontWeight: 500, cursor: 'pointer',
                }}
              >
                {t('common.back_to_login')}
              </button>
              <button
                onClick={this.handleReload}
                style={{
                  padding: '10px 20px', borderRadius: 8, border: 'none',
                  backgroundColor: 'var(--brand, #6b7ff0)', color: '#fff',
                  fontSize: 13, fontWeight: 600, cursor: 'pointer',
                }}
              >
                {t('common.reload_page')}
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}


/** Functional wrapper that provides i18n `t` to the class-based ErrorBoundary. */
export default function ClientErrorBoundaryWithI18n(props) {
  const t = useT();
  return <ClientErrorBoundary {...props} t={t} />;
}
